import React, { useState, useEffect, useCallback } from 'react';
import {
    Gift,
    Copy,
    Check,
    DollarSign,
    Users,
    TrendingUp,
    Calendar,
    AlertCircle,
    Loader2,
    ChevronLeft,
    ChevronRight,
    ExternalLink
} from 'lucide-react';
import {
    fetchAffiliateStatus,
    signupAsAffiliate,
    fetchReferralLink,
    fetchReferrals,
    fetchCommissions,
    fetchPayoutSettings,
    initiateStripeConnect
} from '../api/affiliate';

const AffiliatePortal = ({ userMeta }) => {
    const [selectedTab, setSelectedTab] = useState('dashboard');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Affiliate status
    const [isAffiliate, setIsAffiliate] = useState(false);
    const [affiliateData, setAffiliateData] = useState(null);

    // Signup form
    const [showSignupForm, setShowSignupForm] = useState(false);
    const [signupForm, setSignupForm] = useState({ first_name: '', last_name: '' });
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [signupError, setSignupError] = useState(null);

    // Referrals
    const [referrals, setReferrals] = useState([]);
    const [referralsTotal, setReferralsTotal] = useState(0);
    const [referralsPage, setReferralsPage] = useState(1);
    const [referralsFilter, setReferralsFilter] = useState('');
    const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);

    // Commissions
    const [commissions, setCommissions] = useState([]);
    const [commissionsSummary, setCommissionsSummary] = useState(null);
    const [commissionsTotal, setCommissionsTotal] = useState(0);
    const [commissionsPage, setCommissionsPage] = useState(1);
    const [commissionsFilter, setCommissionsFilter] = useState('');
    const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);

    // Payout settings
    const [payoutSettings, setPayoutSettings] = useState(null);
    const [isConnectingStripe, setIsConnectingStripe] = useState(false);

    // Copy to clipboard
    const [copiedKey, setCopiedKey] = useState(null);

    const copyToClipboard = async (text, keyId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(keyId);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (e) {
            console.error('Failed to copy', e);
        }
    };

    // Load affiliate status
    const loadAffiliateStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchAffiliateStatus();
            if (data.is_affiliate) {
                setIsAffiliate(true);
                setAffiliateData(data);
            } else {
                setIsAffiliate(false);
                setAffiliateData(null);
            }
        } catch (err) {
            setError(err.message || 'Failed to load affiliate status');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load referrals
    const loadReferrals = useCallback(async () => {
        if (!isAffiliate) return;
        setIsLoadingReferrals(true);
        try {
            const data = await fetchReferrals({
                status: referralsFilter || undefined,
                page: referralsPage,
                limit: 10
            });
            setReferrals(data.referrals || []);
            setReferralsTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load referrals', err);
        } finally {
            setIsLoadingReferrals(false);
        }
    }, [isAffiliate, referralsFilter, referralsPage]);

    // Load commissions
    const loadCommissions = useCallback(async () => {
        if (!isAffiliate) return;
        setIsLoadingCommissions(true);
        try {
            const data = await fetchCommissions({
                status: commissionsFilter || undefined,
                page: commissionsPage,
                limit: 10
            });
            setCommissions(data.commissions || []);
            setCommissionsSummary(data.summary || null);
            setCommissionsTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load commissions', err);
        } finally {
            setIsLoadingCommissions(false);
        }
    }, [isAffiliate, commissionsFilter, commissionsPage]);

    // Load payout settings
    const loadPayoutSettings = useCallback(async () => {
        if (!isAffiliate) return;
        try {
            const data = await fetchPayoutSettings();
            setPayoutSettings(data);
        } catch (err) {
            console.error('Failed to load payout settings', err);
        }
    }, [isAffiliate]);

    useEffect(() => {
        loadAffiliateStatus();
    }, [loadAffiliateStatus]);

    useEffect(() => {
        if (isAffiliate && selectedTab === 'referrals') {
            loadReferrals();
        }
    }, [isAffiliate, selectedTab, loadReferrals]);

    useEffect(() => {
        if (isAffiliate && selectedTab === 'commissions') {
            loadCommissions();
        }
    }, [isAffiliate, selectedTab, loadCommissions]);

    useEffect(() => {
        if (isAffiliate && selectedTab === 'settings') {
            loadPayoutSettings();
        }
    }, [isAffiliate, selectedTab, loadPayoutSettings]);

    // Handle affiliate signup
    const handleSignup = async (e) => {
        e.preventDefault();
        setIsSigningUp(true);
        setSignupError(null);
        try {
            const data = await signupAsAffiliate(signupForm);
            setIsAffiliate(true);
            setAffiliateData({
                is_affiliate: true,
                affiliate_id: data.affiliate_id,
                referral_link: data.referral_link,
                referral_code: data.referral_code,
                status: data.status,
                stats: {
                    total_referrals: 0,
                    active_referrals: 0,
                    pending_commissions: 0,
                    paid_commissions: 0,
                    total_earned: 0
                },
                created_at: data.created_at
            });
            setShowSignupForm(false);
        } catch (err) {
            setSignupError(err.message || 'Failed to join affiliate program');
        } finally {
            setIsSigningUp(false);
        }
    };

    // Handle Stripe Connect
    const handleConnectStripe = async () => {
        setIsConnectingStripe(true);
        try {
            // Request Stripe Connect OAuth URL from backend
            const response = await initiateStripeConnect();
            if (response.connect_url) {
                window.location.href = response.connect_url;
            }
        } catch (err) {
            console.error('Failed to initiate Stripe Connect', err);
        } finally {
            setIsConnectingStripe(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-8 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">Unable to Load Affiliate Program</h3>
                <p className="text-slate-500 mb-4">{error}</p>
                <button
                    onClick={loadAffiliateStatus}
                    className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    // Not an affiliate - show signup CTA
    if (!isAffiliate) {
        return (
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold dark:text-white text-slate-900">Affiliate Program</h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1">Earn 25% recurring commissions on every referral</p>
                    </div>
                </div>

                {!showSignupForm ? (
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-8 text-center">
                        <div className="w-16 h-16 bg-sky-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Gift className="w-8 h-8 text-sky-500" />
                        </div>
                        <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">Become an Affiliate</h2>
                        <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                            Share CloudMortgage with your network and earn 25% recurring commissions on every subscription from your referrals.
                        </p>
                        <div className="grid md:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
                            <div className="dark:bg-slate-800/50 bg-slate-50 rounded-lg p-4">
                                <p className="text-2xl font-bold text-sky-500">25%</p>
                                <p className="text-sm text-slate-500">Commission Rate</p>
                            </div>
                            <div className="dark:bg-slate-800/50 bg-slate-50 rounded-lg p-4">
                                <p className="text-2xl font-bold text-green-500">Recurring</p>
                                <p className="text-sm text-slate-500">Monthly Payouts</p>
                            </div>
                            <div className="dark:bg-slate-800/50 bg-slate-50 rounded-lg p-4">
                                <p className="text-2xl font-bold text-violet-500">$50</p>
                                <p className="text-sm text-slate-500">Min. Payout</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowSignupForm(true)}
                            className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors font-medium"
                        >
                            Join the Affiliate Program
                        </button>
                    </div>
                ) : (
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-8 max-w-md mx-auto">
                        <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-6">Join the Affiliate Program</h2>
                        {signupError && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                                <p className="text-sm text-red-600 dark:text-red-400">{signupError}</p>
                            </div>
                        )}
                        <form onSubmit={handleSignup} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={signupForm.first_name}
                                    onChange={(e) => setSignupForm({ ...signupForm, first_name: e.target.value })}
                                    className="w-full px-4 py-2 dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={signupForm.last_name}
                                    onChange={(e) => setSignupForm({ ...signupForm, last_name: e.target.value })}
                                    className="w-full px-4 py-2 dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowSignupForm(false)}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-900 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSigningUp}
                                    className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isSigningUp ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Joining...
                                        </>
                                    ) : (
                                        'Join Program'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        );
    }

    // Affiliate dashboard
    const stats = affiliateData?.stats || {};

    return (
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold dark:text-white text-slate-900">Affiliate Program</h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">Track your referrals and commissions</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b dark:border-slate-800 border-slate-200">
                {[
                    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
                    { id: 'referrals', label: 'Referrals', icon: Users },
                    { id: 'commissions', label: 'Commissions', icon: DollarSign },
                    { id: 'settings', label: 'Settings', icon: Calendar }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setSelectedTab(tab.id)}
                        className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                            selectedTab === tab.id
                                ? 'text-sky-600 dark:text-sky-400'
                                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </div>
                        {selectedTab === tab.id && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"></div>
                        )}
                    </button>
                ))}
            </div>

            {/* Dashboard Tab */}
            {selectedTab === 'dashboard' && (
                <div className="space-y-6">
                    {/* Stats Cards */}
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-green-500/10 rounded-lg">
                                    <DollarSign className="w-5 h-5 text-green-500" />
                                </div>
                                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Earned</h3>
                            </div>
                            <p className="text-3xl font-bold dark:text-white text-slate-900">
                                {formatCurrency(stats.total_earned)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">All time</p>
                        </div>

                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-amber-500/10 rounded-lg">
                                    <TrendingUp className="w-5 h-5 text-amber-500" />
                                </div>
                                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">Pending</h3>
                            </div>
                            <p className="text-3xl font-bold dark:text-white text-slate-900">
                                {formatCurrency(stats.pending_commissions)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">Awaiting payout</p>
                        </div>

                        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-sky-500/10 rounded-lg">
                                    <Users className="w-5 h-5 text-sky-500" />
                                </div>
                                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400">Referrals</h3>
                            </div>
                            <p className="text-3xl font-bold dark:text-white text-slate-900">
                                {stats.total_referrals || 0}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{stats.active_referrals || 0} active</p>
                        </div>
                    </div>

                    {/* Referral Link */}
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Your Referral Link</h3>
                        <div className="dark:bg-slate-950 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                                <code className="flex-1 font-mono text-sm dark:text-slate-200 text-slate-800 break-all">
                                    {affiliateData?.referral_link || 'Loading...'}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(affiliateData?.referral_link, 'referral')}
                                    className="p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors flex-shrink-0"
                                    title="Copy to clipboard"
                                >
                                    {copiedKey === 'referral' ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
                            Share this link to earn 25% commission on every subscription!
                        </p>
                    </div>

                    {/* How it works */}
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">How It Works</h3>
                        <div className="grid md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="w-10 h-10 bg-sky-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-sky-500 font-bold">1</span>
                                </div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Share your link</p>
                            </div>
                            <div className="text-center">
                                <div className="w-10 h-10 bg-sky-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-sky-500 font-bold">2</span>
                                </div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">They sign up</p>
                            </div>
                            <div className="text-center">
                                <div className="w-10 h-10 bg-sky-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-sky-500 font-bold">3</span>
                                </div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">They subscribe</p>
                            </div>
                            <div className="text-center">
                                <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-green-500 font-bold">4</span>
                                </div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">You earn 25%</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Referrals Tab */}
            {selectedTab === 'referrals' && (
                <div className="space-y-6">
                    {/* Filter */}
                    <div className="flex items-center gap-4">
                        <select
                            value={referralsFilter}
                            onChange={(e) => {
                                setReferralsFilter(e.target.value);
                                setReferralsPage(1);
                            }}
                            className="px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">All Referrals</option>
                            <option value="active">Active</option>
                            <option value="churned">Churned</option>
                        </select>
                        <span className="text-sm text-slate-500">{referralsTotal} total</span>
                    </div>

                    {/* Table */}
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                        {isLoadingReferrals ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
                            </div>
                        ) : referrals.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                <p className="text-slate-500">No referrals yet. Share your link to get started!</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="dark:bg-slate-800/50 bg-slate-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Tier</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Signed Up</th>
                                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Commissions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                    {referrals.map((referral) => (
                                        <tr key={referral.referral_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="px-6 py-4 text-sm dark:text-slate-200 text-slate-700">
                                                {referral.customer_email}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    referral.status === 'active'
                                                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                                        : 'bg-slate-500/10 text-slate-600 dark:text-slate-400'
                                                }`}>
                                                    {referral.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm dark:text-slate-200 text-slate-700">
                                                {referral.subscription_tier || 'Free'}
                                            </td>
                                            <td className="px-6 py-4 text-sm dark:text-slate-200 text-slate-700">
                                                {formatDate(referral.signed_up_at)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right dark:text-slate-200 text-slate-700 font-medium">
                                                {formatCurrency(referral.total_commissions)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {referralsTotal > 10 && (
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => setReferralsPage((p) => Math.max(1, p - 1))}
                                disabled={referralsPage === 1}
                                className="flex items-center gap-2 px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                            <span className="text-sm text-slate-500">
                                Page {referralsPage} of {Math.ceil(referralsTotal / 10)}
                            </span>
                            <button
                                onClick={() => setReferralsPage((p) => p + 1)}
                                disabled={referralsPage >= Math.ceil(referralsTotal / 10)}
                                className="flex items-center gap-2 px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg disabled:opacity-50"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Commissions Tab */}
            {selectedTab === 'commissions' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    {commissionsSummary && (
                        <div className="grid md:grid-cols-4 gap-4">
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                                <p className="text-sm text-slate-500 mb-1">Pending</p>
                                <p className="text-xl font-bold text-amber-500">{formatCurrency(commissionsSummary.pending)}</p>
                            </div>
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                                <p className="text-sm text-slate-500 mb-1">Due</p>
                                <p className="text-xl font-bold text-sky-500">{formatCurrency(commissionsSummary.due)}</p>
                            </div>
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                                <p className="text-sm text-slate-500 mb-1">Paid This Month</p>
                                <p className="text-xl font-bold text-green-500">{formatCurrency(commissionsSummary.paid_this_month)}</p>
                            </div>
                            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
                                <p className="text-sm text-slate-500 mb-1">Total Paid</p>
                                <p className="text-xl font-bold dark:text-white text-slate-900">{formatCurrency(commissionsSummary.paid_total)}</p>
                            </div>
                        </div>
                    )}

                    {/* Filter */}
                    <div className="flex items-center gap-4">
                        <select
                            value={commissionsFilter}
                            onChange={(e) => {
                                setCommissionsFilter(e.target.value);
                                setCommissionsPage(1);
                            }}
                            className="px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">All Commissions</option>
                            <option value="pending">Pending</option>
                            <option value="due">Due</option>
                            <option value="paid">Paid</option>
                        </select>
                        <span className="text-sm text-slate-500">{commissionsTotal} total</span>
                    </div>

                    {/* Table */}
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                        {isLoadingCommissions ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
                            </div>
                        ) : commissions.length === 0 ? (
                            <div className="text-center py-12">
                                <DollarSign className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                <p className="text-slate-500">No commissions yet. Refer someone to get started!</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="dark:bg-slate-800/50 bg-slate-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Sale</th>
                                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Rate</th>
                                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Commission</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y dark:divide-slate-800 divide-slate-200">
                                    {commissions.map((commission) => (
                                        <tr key={commission.commission_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="px-6 py-4 text-sm dark:text-slate-200 text-slate-700">
                                                {formatDate(commission.created_at)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    commission.status === 'paid'
                                                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                                        : commission.status === 'due'
                                                        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                                                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                                }`}>
                                                    {commission.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right dark:text-slate-200 text-slate-700">
                                                {formatCurrency(commission.sale_amount)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right dark:text-slate-200 text-slate-700">
                                                {((commission.commission_rate || 0.25) * 100).toFixed(0)}%
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-green-600 dark:text-green-400">
                                                {formatCurrency(commission.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {commissionsTotal > 10 && (
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => setCommissionsPage((p) => Math.max(1, p - 1))}
                                disabled={commissionsPage === 1}
                                className="flex items-center gap-2 px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                            <span className="text-sm text-slate-500">
                                Page {commissionsPage} of {Math.ceil(commissionsTotal / 10)}
                            </span>
                            <button
                                onClick={() => setCommissionsPage((p) => p + 1)}
                                disabled={commissionsPage >= Math.ceil(commissionsTotal / 10)}
                                className="flex items-center gap-2 px-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg disabled:opacity-50"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Settings Tab */}
            {selectedTab === 'settings' && (
                <div className="space-y-6">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 max-w-xl">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-6">Payout Settings</h3>

                        <div className="space-y-4">
                            {/* Stripe Connect Status */}
                            <div className="dark:bg-slate-800/50 bg-slate-50 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            payoutSettings?.stripe_connected
                                                ? 'bg-green-500/10'
                                                : 'bg-slate-200 dark:bg-slate-700'
                                        }`}>
                                            <svg className={`w-5 h-5 ${payoutSettings?.stripe_connected ? 'text-green-500' : 'text-slate-400'}`} viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-medium dark:text-white text-slate-900">
                                                {payoutSettings?.stripe_connected ? 'Stripe Connected' : 'Connect Stripe'}
                                            </p>
                                            <p className="text-sm text-slate-500">
                                                {payoutSettings?.stripe_connected
                                                    ? `Connected as ${payoutSettings.stripe_account_email || 'your account'}`
                                                    : 'Connect your Stripe account to receive payouts'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    {payoutSettings?.stripe_connected ? (
                                        <span className="px-3 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-sm font-medium">
                                            Connected
                                        </span>
                                    ) : (
                                        <button
                                            onClick={handleConnectStripe}
                                            disabled={isConnectingStripe}
                                            className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#5851ea] transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
                                        >
                                            {isConnectingStripe ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Connecting...
                                                </>
                                            ) : (
                                                'Connect with Stripe'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {payoutSettings && (
                                <div className="pt-4 border-t dark:border-slate-800 border-slate-200">
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-slate-500">Minimum Payout</span>
                                        <span className="dark:text-white text-slate-900 font-medium">
                                            {formatCurrency(payoutSettings.minimum_payout)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-slate-500">Next Payout Date</span>
                                        <span className="dark:text-white text-slate-900 font-medium">
                                            {formatDate(payoutSettings.next_payout_date)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Pending Amount</span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                            {formatCurrency(payoutSettings.pending_amount)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info box */}
                    <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-4 max-w-xl">
                        <p className="text-sm text-sky-600 dark:text-sky-400 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                                Payouts are processed monthly via Stripe. You need at least {formatCurrency(payoutSettings?.minimum_payout || 50)} in commissions to receive a payout.
                            </span>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AffiliatePortal;
